/* Trip CN Guide email-code authentication client. */
(function () {
  'use strict';

  var API_BASE = 'https://api.tripcnguide.com/api/v1/auth';
  var TOKEN_KEY = 'tcng_token';
  var state = { isAuthenticated: false, isLoading: true, user: null };
  var initPromise = null;
  var codeTimer = null;
  var modal = null;
  var modalPanel = null;
  var lastFocused = null;
  var closeTimer = null;

  function authError(kind, message, status) {
    var error = new Error(message || 'Something went wrong. Please try again.');
    error.kind = kind || 'unknown';
    error.status = status || 0;
    return error;
  }

  function token() {
    return window.localStorage.getItem(TOKEN_KEY);
  }

  function clearSession() {
    window.localStorage.removeItem(TOKEN_KEY);
    state.isAuthenticated = false;
    state.user = null;
  }

  function notify() {
    document.dispatchEvent(new CustomEvent('tripcn:auth-change', {
      detail: {
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        user: state.user
      }
    }));
  }

  function friendlyMessage(message, status) {
    var value = String(message || '').toLowerCase();
    if (status === 401 || value.indexOf('expired') !== -1 || value.indexOf('\u672a\u767b\u5f55') !== -1) return 'Your session has expired. Please log in again.';
    if (status === 403 || value.indexOf('disabled') !== -1 || value.indexOf('\u7981\u7528') !== -1) return 'This account is currently unavailable. Please contact support.';
    if (status >= 500) return 'The service is temporarily unavailable. Please try again shortly.';
    if (value.indexOf('frequent') !== -1 || value.indexOf('too many') !== -1 || value.indexOf('\u9891\u7e41') !== -1) return 'Please wait a moment before requesting another code.';
    if (value.indexOf('verification code') !== -1 || value.indexOf('code') !== -1 || value.indexOf('\u9a8c\u8bc1\u7801') !== -1) return 'That verification code is incorrect or has expired.';
    if (value.indexOf('email') !== -1 || value.indexOf('\u90ae\u7bb1') !== -1) return 'Check the email address and try again.';
    return 'Something went wrong. Please try again.';
  }

  async function request(path, options) {
    var config = options || {};
    var headers = config.headers || {};
    var savedToken = config.authorized ? token() : null;
    if (savedToken) headers.Authorization = 'Bearer ' + savedToken;
    if (config.body) headers['Content-Type'] = 'application/json';

    var response;
    try {
      response = await window.fetch(API_BASE + path, {
        method: config.method || 'GET',
        headers: headers,
        body: config.body ? JSON.stringify(config.body) : undefined
      });
    } catch (error) {
      throw authError('network', 'Unable to reach the service. Check your connection and try again.');
    }

    var payload;
    try {
      payload = await response.json();
    } catch (error) {
      throw authError('service', 'The service returned an unexpected response.', response.status);
    }

    if (!response.ok || !payload || payload.code !== 200) {
      if (response.status === 401 || payload && payload.code === 401) {
        clearSession();
        notify();
      }
      throw authError(
        'api',
        friendlyMessage(payload && payload.msg, response.status || payload && payload.code),
        response.status || payload && payload.code
      );
    }

    return payload.data;
  }

  async function getCurrentUser() {
    return request('/me', { authorized: true });
  }

  async function initialize() {
    if (initPromise) return initPromise;
    initPromise = (async function () {
      if (!token()) {
        state.isLoading = false;
        notify();
        return state;
      }

      try {
        state.user = await getCurrentUser();
        state.isAuthenticated = true;
      } catch (error) {
        clearSession();
      }

      state.isLoading = false;
      notify();
      return state;
    })();
    return initPromise;
  }

  async function signIn(email, code) {
    var data = await request('/login', {
      method: 'POST',
      body: { email: email, mode: 'code', code: code }
    });

    if (!data || !data.token) {
      throw authError('service', 'The service did not return a valid session. Please try again.');
    }

    window.localStorage.setItem(TOKEN_KEY, data.token);
    state.isAuthenticated = true;
    state.user = { userId: data.userId, email: data.email, nickname: data.nickname };
    notify();

    try {
      state.user = await getCurrentUser();
    } catch (error) {
      clearSession();
      notify();
      throw error;
    }

    state.isLoading = false;
    notify();
    return state.user;
  }

  async function signOut() {
    try {
      if (token()) await request('/logout', { method: 'POST', authorized: true });
    } catch (error) {
      /* The local session must still be cleared. */
    }
    clearSession();
    state.isLoading = false;
    notify();
  }

  function setFieldError(form, field, message) {
    var input = form.querySelector('[name="' + field + '"]');
    var output = form.querySelector('[data-auth-error-for="' + field + '"]');
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
    if (output) output.textContent = message || '';
    return !message;
  }

  function validateField(form, field) {
    var input = form.querySelector('[name="' + field + '"]');
    var value = input ? input.value.trim() : '';
    if (field === 'email') {
      return setFieldError(
        form,
        field,
        !value
          ? 'Enter your email address.'
          : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            ? 'Enter a valid email address.'
            : ''
      );
    }
    if (field === 'code') {
      return setFieldError(
        form,
        field,
        !/^\d{6}$/.test(value) ? 'Enter the 6-digit code from your email.' : ''
      );
    }
    return true;
  }

  function setFormMessage(form, message) {
    var output = form.querySelector('[data-auth-form-error]');
    output.textContent = message || '';
    output.classList.toggle('is-visible', Boolean(message));
  }

  function setStatus(form, message) {
    var output = form.querySelector('[data-auth-status]');
    output.textContent = message || '';
    output.classList.toggle('is-visible', Boolean(message));
  }

  function startCodeCooldown(button, seconds) {
    var remaining = Number(seconds) || 60;
    button.disabled = true;
    button.textContent = 'Resend in ' + remaining + 's';
    window.clearInterval(codeTimer);
    codeTimer = window.setInterval(function () {
      remaining -= 1;
      if (remaining <= 0) {
        window.clearInterval(codeTimer);
        codeTimer = null;
        button.disabled = false;
        button.textContent = 'Send code';
        return;
      }
      button.textContent = 'Resend in ' + remaining + 's';
    }, 1000);
  }

  function openModal(trigger) {
    if (!modal || !modalPanel || state.isAuthenticated) return;
    if (window.tcng) window.tcng.track('login_modal_open');
    window.clearTimeout(closeTimer);
    lastFocused = trigger || document.activeElement;
    modal.hidden = false;
    document.body.classList.add('tc-auth-modal-open');
    window.requestAnimationFrame(function () {
      modal.classList.add('is-open');
      var email = modal.querySelector('[name="email"]');
      var isMobile = window.matchMedia('(max-width: 560px)').matches;
      if (email && !isMobile) email.focus({ preventScroll: true });
      else modalPanel.focus({ preventScroll: true });
    });
  }

  function closeModal(restoreFocus) {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('tc-auth-modal-open');
    if (!state.isAuthenticated) {
      document.dispatchEvent(new CustomEvent('tripcn:auth-modal-close'));
    }
    closeTimer = window.setTimeout(function () {
      modal.hidden = true;
      if (restoreFocus !== false && lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }, 130);
  }

  function keepFocusInsideModal(event) {
    if (event.key !== 'Tab' || !modal || modal.hidden) return;
    var focusable = modalPanel.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initModal() {
    modal = document.querySelector('[data-auth-modal]');
    if (!modal) return;
    modalPanel = modal.querySelector('.tc-auth-modal__panel');
    var form = modal.querySelector('[data-auth-form]');
    var sendCode = modal.querySelector('[data-auth-send-code]');

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-auth-open]');
      if (!trigger) return;
      event.preventDefault();
      openModal(trigger);
    });

    modal.querySelector('[data-auth-close]').addEventListener('click', function () {
      closeModal(true);
    });

    modalPanel.addEventListener('keydown', function (event) {
      keepFocusInsideModal(event);
      event.stopPropagation();
    });

    modalPanel.addEventListener('keyup', function (event) {
      event.stopPropagation();
    });

    ['email', 'code'].forEach(function (field) {
      var input = form.querySelector('[name="' + field + '"]');
      input.addEventListener('blur', function () {
        if (input.value.trim()) validateField(form, field);
      });
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true') validateField(form, field);
        setFormMessage(form, '');
      });
    });

    sendCode.addEventListener('click', async function () {
      setFormMessage(form, '');
      setStatus(form, '');
      if (!validateField(form, 'email')) return;

      sendCode.disabled = true;
      sendCode.textContent = 'Sending...';
      try {
        var email = form.elements.email.value.trim();
        var result = await request('/send-code', {
          method: 'POST',
          body: { email: email }
        });
        startCodeCooldown(sendCode, result && result.cooldownSeconds);
        if (window.tcng) window.tcng.track('login_code_sent');
        setStatus(form, 'Verification code sent to ' + email + '.');
        form.elements.code.focus();
      } catch (error) {
        sendCode.disabled = false;
        sendCode.textContent = 'Send code';
        if (window.tcng) {
          window.tcng.track('login_code_failed', {
            error_message: String((error && error.message) || 'unknown').slice(0, 100)
          });
        }
        setFormMessage(form, error.message);
      }
    });

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      var validEmail = validateField(form, 'email');
      var validCode = validateField(form, 'code');
      if (!validEmail || !validCode) return;

      setFormMessage(form, '');
      var submit = form.querySelector('[data-auth-submit]');
      submit.disabled = true;
      submit.textContent = 'Logging in...';
      try {
        await signIn(form.elements.email.value.trim(), form.elements.code.value.trim());
        if (window.tcng) window.tcng.track('login', { method: 'email_code' });
        document.querySelectorAll('[data-auth-required]').forEach(function (element) {
          element.classList.add('tc-auth-guard--ready');
        });
        closeModal(false);
        form.reset();
        setStatus(form, '');
      } catch (error) {
        if (window.tcng) {
          window.tcng.track('login_failed', {
            error_message: String((error && error.message) || 'unknown').slice(0, 100)
          });
        }
        setFormMessage(form, error.message);
      } finally {
        submit.disabled = false;
        submit.textContent = 'Continue';
      }
    });
  }

  function initHeaderAccount() {
    var hosts = document.querySelectorAll('[data-auth-header-account]');
    if (!hosts.length) return;

    function render() {
      hosts.forEach(function (host) {
        host.textContent = '';

        if (!state.isAuthenticated || !state.user) {
          var login = document.createElement('button');
          login.type = 'button';
          login.textContent = 'Log in';
          login.className = 'header__auth-link';
          login.setAttribute('data-auth-open', '');
          host.appendChild(login);
          return;
        }

        var details = document.createElement('details');
        details.className = 'header__user-menu';
        var summary = document.createElement('summary');
        summary.className = 'header__user-summary';
        var avatar = document.createElement('span');
        avatar.className = 'header__avatar';
        avatar.textContent = (state.user.nickname || state.user.email || 'U').charAt(0).toUpperCase();
        var name = document.createElement('span');
        name.className = 'header__user-name';
        name.textContent = state.user.nickname || state.user.email;
        summary.append(avatar, name);
        details.appendChild(summary);

        var menu = document.createElement('div');
        menu.className = 'header__user-dropdown';
        var email = document.createElement('span');
        email.className = 'header__user-email';
        email.textContent = state.user.email || '';
        var myCoupons = document.createElement('a');
        myCoupons.href = '/pages/my-coupons';
        myCoupons.textContent = 'My Coupons';
        var logout = document.createElement('button');
        logout.type = 'button';
        logout.textContent = 'Log out';
        logout.addEventListener('click', async function () {
          logout.disabled = true;
          await signOut();
          // The header re-renders from the auth-change event, so stay put.
          // Only leave when this page is gated behind login and its contents
          // are no longer viewable.
          if (document.querySelector('[data-auth-required]')) {
            window.location.assign('/');
          }
        });
        menu.append(email, myCoupons, logout);
        details.appendChild(menu);
        host.appendChild(details);
      });
    }

    // The dropdown is a native <details>, which only toggles on its own summary.
    // Delegate on the document so an outside click or Esc collapses it too.
    function collapseMenus(except) {
      document.querySelectorAll('.header__user-menu[open]').forEach(function (menu) {
        if (menu !== except) menu.open = false;
      });
    }

    function openMenuFor(node) {
      return node && typeof node.closest === 'function'
        ? node.closest('.header__user-menu[open]')
        : null;
    }

    // A click on the summary is still dispatched while [open] reflects the old
    // state, so the menu being toggled is never the one we collapse here.
    document.addEventListener('click', function (event) {
      collapseMenus(openMenuFor(event.target));
    });

    document.addEventListener('focusin', function (event) {
      collapseMenus(openMenuFor(event.target));
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openMenus = document.querySelectorAll('.header__user-menu[open]');
      if (!openMenus.length) return;
      openMenus.forEach(function (menu) {
        menu.open = false;
        var summary = menu.querySelector('summary');
        if (summary) summary.focus();
      });
    });

    document.addEventListener('tripcn:auth-change', render);
    render();
  }

  function initGuards() {
    var guarded = document.querySelectorAll('[data-auth-required]');
    if (!guarded.length) return;

    initialize().then(function () {
      if (state.isAuthenticated) {
        guarded.forEach(function (element) {
          element.classList.add('tc-auth-guard--ready');
        });
        return;
      }
      openModal(null);
    });
  }

  window.TripCNAuth = {
    getState: function () {
      return {
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        user: state.user
      };
    },
    initialize: initialize,
    signIn: signIn,
    signOut: signOut,
    getCurrentUser: getCurrentUser,
    getAccessToken: token,
    openLogin: function () {
      openModal(null);
    },
    // Resolves with the user once authenticated: immediately when a session
    // already exists, otherwise after they complete the login modal. Resolves
    // with null if they dismiss the modal instead, so callers can just check
    // the value rather than handling a rejection.
    requireLogin: function () {
      return initialize().then(function () {
        if (state.isAuthenticated) return state.user;
        return new Promise(function (resolve) {
          function cleanup() {
            document.removeEventListener('tripcn:auth-change', onChange);
            document.removeEventListener('tripcn:auth-modal-close', onDismiss);
          }
          function onChange(event) {
            if (!event.detail || !event.detail.isAuthenticated) return;
            cleanup();
            resolve(state.user);
          }
          function onDismiss() {
            cleanup();
            resolve(null);
          }
          document.addEventListener('tripcn:auth-change', onChange);
          document.addEventListener('tripcn:auth-modal-close', onDismiss);
          openModal(null);
        });
      });
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    initModal();
    initHeaderAccount();
    initGuards();
  });

  window.addEventListener('pagehide', function () {
    if (codeTimer) window.clearInterval(codeTimer);
    if (closeTimer) window.clearTimeout(closeTimer);
  });

  initialize();
})();
